import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useCRMStore = create(
  persist(
    (set, get) => ({
      // Customers
      customers: [
        { id: 1, name: 'Acme Corp', email: 'contact@acme.com', phone: '555-0101', company: 'Acme Corp', status: 'Active', value: 125000, industry: 'Tech', createdAt: new Date().toISOString() },
        { id: 2, name: 'TechCorp Solutions', email: 'sales@techcorp.com', phone: '555-0102', company: 'TechCorp', status: 'Active', value: 85000, industry: 'Software', createdAt: new Date().toISOString() },
        { id: 3, name: 'GlobalSys Inc', email: 'info@globalsys.com', phone: '555-0103', company: 'GlobalSys', status: 'Prospect', value: 220000, industry: 'Consulting', createdAt: new Date().toISOString() },
      ],

      // Leads
      leads: [
        { id: 1, name: 'John Smith', email: 'john@example.com', phone: '555-0201', company: 'StartUp X', source: 'Website', status: 'New', score: 85, createdAt: new Date().toISOString() },
        { id: 2, name: 'Sarah Johnson', email: 'sarah@example.com', phone: '555-0202', company: 'Growth Co', source: 'LinkedIn', status: 'Contacted', score: 72, createdAt: new Date().toISOString() },
        { id: 3, name: 'Mike Brown', email: 'mike@example.com', phone: '555-0203', company: 'Enterprise Ltd', source: 'Referral', status: 'Qualified', score: 95, createdAt: new Date().toISOString() },
      ],

      // Opportunities
      opportunities: [
        { id: 1, title: 'Acme Corp - Annual Contract', customerId: 1, value: 125000, stage: 'Proposal', probability: 85, closeDate: '2026-06-30', description: 'Annual software contract renewal', createdAt: new Date().toISOString() },
        { id: 2, title: 'TechCorp - New Implementation', customerId: 2, value: 85000, stage: 'Negotiation', probability: 60, closeDate: '2026-05-15', description: 'New system implementation and training', createdAt: new Date().toISOString() },
        { id: 3, title: 'GlobalSys - Consulting Services', customerId: 3, value: 220000, stage: 'Prospect', probability: 30, closeDate: '2026-07-30', description: 'Multi-phase consulting engagement', createdAt: new Date().toISOString() },
      ],

      // Activities
      activities: [
        { id: 1, type: 'Call', customerId: 1, description: 'Discussed renewal terms and pricing', date: new Date().toISOString(), user: 'Admin', status: 'Completed' },
        { id: 2, type: 'Email', customerId: 2, description: 'Sent proposal for implementation', date: new Date().toISOString(), user: 'Admin', status: 'Completed' },
        { id: 3, type: 'Meeting', customerId: 3, description: 'Initial discovery meeting scheduled', date: new Date(Date.now() + 3600000).toISOString(), user: 'Admin', status: 'Scheduled' },
      ],

      // Contacts
      contacts: [
        { id: 1, name: 'Jane Doe', title: 'CTO', company: 'Acme Corp', email: 'jane@acme.com', phone: '555-0301', customerId: 1, createdAt: new Date().toISOString() },
        { id: 2, name: 'Bob Wilson', title: 'VP Sales', company: 'TechCorp', email: 'bob@techcorp.com', phone: '555-0302', customerId: 2, createdAt: new Date().toISOString() },
        { id: 3, name: 'Alice Kumar', title: 'Director', company: 'GlobalSys', email: 'alice@globalsys.com', phone: '555-0303', customerId: 3, createdAt: new Date().toISOString() },
      ],

      // Pipeline Stages
      pipelineStages: [
        { stage: 'Lead', count: 187, value: 93500 },
        { stage: 'Prospect', count: 85, value: 382500 },
        { stage: 'Negotiation', count: 34, value: 459000 },
        { stage: 'Proposal', count: 28, value: 315000 },
      ],

      // Customer Actions
      addCustomer: (customer) => set((state) => ({
        customers: [...state.customers, { ...customer, id: Math.max(...state.customers.map(c => c.id), 0) + 1, createdAt: new Date().toISOString() }]
      })),

      updateCustomer: (customerId, updates) => set((state) => ({
        customers: state.customers.map(c => c.id === customerId ? { ...c, ...updates } : c)
      })),

      deleteCustomer: (customerId) => set((state) => ({
        customers: state.customers.filter(c => c.id !== customerId)
      })),

      // Lead Actions
      addLead: (lead) => set((state) => ({
        leads: [...state.leads, { ...lead, id: Math.max(...state.leads.map(l => l.id), 0) + 1, createdAt: new Date().toISOString() }]
      })),

      updateLead: (leadId, updates) => set((state) => ({
        leads: state.leads.map(l => l.id === leadId ? { ...l, ...updates } : l)
      })),

      deleteLead: (leadId) => set((state) => ({
        leads: state.leads.filter(l => l.id !== leadId)
      })),

      convertLeadToCustomer: (leadId, leadData) => set((state) => {
        const lead = state.leads.find(l => l.id === leadId)
        if (!lead) return state
        return {
          customers: [...state.customers, { id: Math.max(...state.customers.map(c => c.id), 0) + 1, ...lead, status: 'New', value: 0, createdAt: new Date().toISOString() }],
          leads: state.leads.filter(l => l.id !== leadId)
        }
      }),

      // Opportunity Actions
      addOpportunity: (opportunity) => set((state) => ({
        opportunities: [...state.opportunities, { ...opportunity, id: Math.max(...state.opportunities.map(o => o.id), 0) + 1, createdAt: new Date().toISOString() }]
      })),

      updateOpportunity: (opportunityId, updates) => set((state) => ({
        opportunities: state.opportunities.map(o => o.id === opportunityId ? { ...o, ...updates } : o)
      })),

      deleteOpportunity: (opportunityId) => set((state) => ({
        opportunities: state.opportunities.filter(o => o.id !== opportunityId)
      })),

      // Activity Actions
      addActivity: (activity) => set((state) => ({
        activities: [...state.activities, { ...activity, id: Math.max(...state.activities.map(a => a.id), 0) + 1 }]
      })),

      updateActivity: (activityId, updates) => set((state) => ({
        activities: state.activities.map(a => a.id === activityId ? { ...a, ...updates } : a)
      })),

      deleteActivity: (activityId) => set((state) => ({
        activities: state.activities.filter(a => a.id !== activityId)
      })),

      // Contact Actions
      addContact: (contact) => set((state) => ({
        contacts: [...state.contacts, { ...contact, id: Math.max(...state.contacts.map(c => c.id), 0) + 1, createdAt: new Date().toISOString() }]
      })),

      updateContact: (contactId, updates) => set((state) => ({
        contacts: state.contacts.map(c => c.id === contactId ? { ...c, ...updates } : c)
      })),

      deleteContact: (contactId) => set((state) => ({
        contacts: state.contacts.filter(c => c.id !== contactId)
      })),

      // Statistics
      getStats: () => {
        const state = get()
        const totalCustomers = state.customers.length
        const activePipeline = state.opportunities.reduce((sum, o) => sum + o.value, 0)
        const avgDealSize = state.opportunities.length > 0 ? activePipeline / state.opportunities.length : 0
        const conversionRate = Math.round((state.customers.length / (state.customers.length + state.leads.length)) * 100)

        return {
          totalCustomers,
          activePipeline,
          avgDealSize: Math.round(avgDealSize),
          conversionRate,
          totalLeads: state.leads.length,
          totalOpportunities: state.opportunities.length,
          totalContacts: state.contacts.length,
          totalActivities: state.activities.length,
        }
      },

      // Get all data
      getAllData: () => {
        const state = get()
        return {
          customers: state.customers,
          leads: state.leads,
          opportunities: state.opportunities,
          activities: state.activities,
          contacts: state.contacts,
        }
      },
    }),
    {
      name: 'crm-store',
      getStorage: () => localStorage,
    }
  )
)
